import { ApplePay, Client, DataCollector, PayPalCheckout, usBankAccount } from 'braintree-web';
import applePay from 'braintree-web/apple-pay';
import client from 'braintree-web/client';
import dataCollector from 'braintree-web/data-collector';
import paypalCheckout from 'braintree-web/paypal-checkout';
import { defineStore } from 'pinia';
import { watch } from 'vue';

import { getBraintreeToken } from '@/api/checkout/payment';
import { UseCallback, useCallback } from '@/composables/useCallback';
import { NutsAddress } from '@/utils/address';

interface BraintreeState {
  applePay?: ApplePay;
  applePayLoader?: UseCallback;
  auth?: string;
  authLoader?: UseCallback;
  client?: Client;
  dataCollector?: DataCollector;
  payPal?: PayPalCheckout;
  payPalLoader?: UseCallback;
}

export interface BraintreeAddress {
  streetAddress: string;
  extendedAddress?: string;
  locality: string;
  region: string;
  postalCode: string;
}

export interface BaseUSBankAccountDetails {
  accountNumber: string;
  routingNumber: string;
  accountType: 'checking' | 'savings';
  billingAddress: BraintreeAddress;
}

export interface USBankAccountDetailsPersonal extends BaseUSBankAccountDetails {
  ownershipType: 'personal';
  firstName: string;
  lastName: string;
}

export interface USBankAccountDetailsBusiness extends BaseUSBankAccountDetails {
  ownershipType: 'business';
  businessName: string;
}

export interface TokenizedUSBankAccountResponse {
  nonce: string;
  details: {
    cardholderName: string;
    cardType: string;
    expirationMonth: string;
    expirationYear: string;
    lastFour: string;
  };
  billingAddress: NutsAddress;
  setAsDefault?: boolean;
}

const watchLoader = (loader: UseCallback) =>
  new Promise<true>((resolve) => {
    const done = watch(
      () => loader.isPending,
      (inProgress) => {
        if (!inProgress) {
          done();
          resolve(true);
        }
      },
    );
  });

export const useBraintree = defineStore('braintree', {
  actions: {
    async init() {
      if (!this.client) {
        const authorization = (await this.loadAuthToken()) ?? '';
        this.client = await client.create({ authorization });
      }
      return this.client;
    },

    async initApplePay() {
      if (!this.applePayLoader) {
        const self = this;
        this.applePayLoader = useCallback(async () => {
          const braintreeClient = await self.init();
          self.applePay = await applePay.create({
            client: braintreeClient,
          });
          return self.applePay;
        });
      }
      if (!this.applePay) {
        if (this.applePayLoader.isPending) {
          await watchLoader(this.applePayLoader);
        } else if (!this.applePayLoader.result) {
          await this.applePayLoader.execute();
        }
      }
      return this.applePay;
    },

    async initDataCollector() {
      if (!this.dataCollector) {
        const authorization = (await this.loadAuthToken()) ?? '';
        this.dataCollector = await dataCollector.create({
          authorization,
        } as any); // @types/braintree-web is wrong, thinks `authorization` isn't allowed :(
      }
      return this.dataCollector;
    },

    async initPayPal(autoSetDataUserIdToken = true): Promise<PayPalCheckout | undefined> {
      if (!this.payPalLoader) {
        const self = this;
        this.payPalLoader = useCallback(async () => {
          const braintreeClient = await self.init();
          const payPal = await paypalCheckout.create({
            // @ts-ignore
            autoSetDataUserIdToken,
            client: braintreeClient,
          });
          // @ts-ignore
          await payPal.loadPayPalSDK({ intent: 'authorize' });
          // @ts-ignore
          self.payPal = payPal;
          return self.payPal;
        });
      }
      if (!this.payPal) {
        if (this.payPalLoader.isPending) {
          await watchLoader(this.payPalLoader);
        } else if (!this.payPalLoader.result) {
          await this.payPalLoader.execute();
        }
      }
      return this.payPal;
    },

    async loadAuthToken(): Promise<string | undefined> {
      if (!this.authLoader) {
        this.authLoader = useCallback(getBraintreeToken);
      }
      if (!this.auth) {
        if (this.authLoader.isPending) {
          await watchLoader(this.authLoader);
        } else if (!this.authLoader.result) {
          this.auth = await this.authLoader.execute();
        }
      }
      return this.auth;
    },

    async tokenizeUSBankAccountDetails(
      bankDetails: USBankAccountDetailsPersonal | USBankAccountDetailsBusiness,
      mandateText: string,
    ): Promise<TokenizedUSBankAccountResponse> {
      const cli = await this.init();
      // @ts-ignore (@types/braintree-web is wrong. per BT's ACH docs, Promises are supported)
      const usBankAccountInstance: USBankAccount = await usBankAccount.create({ client: cli });
      // @ts-ignore (@types/braintree-web is wrong. per BT's ACH docs, Promises are supported)
      const tokenizedBankDetails = await usBankAccountInstance.tokenize({
        bankDetails,
        mandateText,
        bankLogin: undefined,
      });

      const fullName =
        'firstName' in bankDetails ? `${bankDetails.firstName} ${bankDetails.lastName}` : '';
      const company = 'businessName' in bankDetails ? bankDetails.businessName : '';

      const synthesizedPaymentMethod = {
        nonce: tokenizedBankDetails.nonce,
        details: {
          cardType: 'US Bank Account',
          cardholderName: fullName ?? company,
          expirationYear: '2999',
          expirationMonth: '12',
          lastFour: '0000',
        },
        billingAddress: {
          name: fullName,
          company,
          street1: bankDetails.billingAddress.streetAddress,
          street2: bankDetails.billingAddress.extendedAddress,
          city: bankDetails.billingAddress.locality,
          state: bankDetails.billingAddress.region,
          postalCode: bankDetails.billingAddress.postalCode,
          country: 'US',
        },
      };
      return synthesizedPaymentMethod;
    },

    async cleanCache() {
      this.payPalLoader = undefined;
      this.payPal = undefined;
      this.applePayLoader = undefined;
      this.applePay = undefined;
      this.authLoader = undefined;
      this.auth = undefined;
      this.dataCollector = undefined;
      this.client = undefined;
    },
  },
  state: (): BraintreeState => ({}),
});

export type BraintreeStore = ReturnType<typeof useBraintree>;
